import session_schema from '~/schemas/session_schema'

/**
 * Local preferences — the settings this browser keeps and nothing else consumes.
 *
 * ⚠️ MOSTLY hand-written, but NOT entirely: `session_type` is spread in from the generated `session_schema` below, because three repos must agree on its options and its default. A contract-owned key's fields may never be hand-edited here — edit `../schema/src/session.ts` and regenerate. The tell is the spread: anything written out longhand in this file is local, anything spread in is contract.
 */
const settings_schema = {
	type: 'object',
	properties: {
		rec_source: {
			type: 'boolean',
			initial: false,
			label: 'Record source video',
			tip: 'Also record the original (unprocessed) input video',
		},
		fps: {
			type: 'number',
			minimum: 1,
			maximum: 30,
			step: 1,
			initial: 30,
			label: 'FPS',
			tip: 'Caps the outgoing frame rate. Capture and animation always run at 30.',
		},
		theme: {
			type: 'string',
			kind: 'select',
			initial: 'light',
			label: 'Theme',
			enum: ['dark', 'light'],
		},
		confirm_unsaved_preset: {
			type: 'boolean',
			initial: true,
			label: 'Ask before leaving an unsaved preset',
		},
		confirm_delete_preset: {
			type: 'boolean',
			initial: true,
			label: 'Ask before deleting a preset',
		},
		confirm_delete_object: {
			type: 'boolean',
			initial: true,
			label: 'Ask before deleting an object',
		},
		confirm_delete_project: {
			type: 'boolean',
			initial: true,
			label: 'Ask before deleting a project',
		},
		confirm_stop_session: {
			type: 'boolean',
			initial: true,
			label: 'Ask before stopping a session',
		},
		confirm_record_no_audio: {
			type: 'boolean',
			initial: true,
			label: 'Ask before recording without a microphone',
		},
		stream_direction: {
			type: 'string',
			kind: 'select',
			initial: 'send_receive',
			label: 'Stream Direction',
			enum: [
				{ id: 'send_receive', label: 'Send & Receive' },
				{ id: 'send_only', label: 'Send Only' },
				{ id: 'receive_only', label: 'Receive Only' },
			],
		},
		show_fps: {
			type: 'boolean',
			initial: false,
			label: 'Show FPS stats',
		},
		show_example_presets: {
			type: 'boolean',
			initial: true,
			label: 'Show example presets',
			tip: 'The Examples folder in the Presets panel, and in autoplay when this desktop runs it.',
		},
		microphone_enabled: {
			type: 'boolean',
			initial: true,
			label: 'Enable Audio',
		},
		// CONTRACT-OWNED — the options and the default live in `../schema/src/session.ts`, because the web client, the daemon and the server must agree on them. It sits in this file so every settings reader keeps finding it where it always was.
		//
		// Spread into a COPY, not referenced: the loop below stamps `name` onto every property, and writing that through to a generated module's export would mutate it for every other importer.
		session_type: { ...session_schema.properties.session_type },
		use_pixi: {
			type: 'boolean',
			initial: true,
			label: 'Graphical editor',
			tip: 'Enable canvas editor with layers and drawing tools. Disable for a lighter video-only pipeline with no compositing.',
		},
		keep_cameras_armed: {
			type: 'boolean',
			initial: true,
			label: 'Keep cameras warm after use',
			tip: 'A camera put on stage is kept warm, so it stays live off stage and switches back instantly.',
		},
		flip_h: {
			type: 'boolean',
			initial: true,
			label: 'Flip Horizontal',
		},
		flip_v: {
			type: 'boolean',
			initial: false,
			label: 'Flip Vertical',
		},
		preset_index_prefix: {
			type: 'boolean',
			initial: false,
			label: 'Number presets',
			tip: 'Prefix each preset name with its 0-based index (dev only)',
		},
		channel: {
			type: 'string',
			initial: '',
			label: 'Session Channel',
			tip: 'Override session channel (dev only)',
			placeholder: 'Leave empty for default',
		},
	},
}

for (const key in settings_schema.properties) {
	const prop = settings_schema.properties[key]
	prop.name = key
}

export default settings_schema
